import { parseAbsolute, parseZonedDateTime, toTime, ZonedDateTime } from '@internationalized/date';
import { AirportListItemType } from '@lp/features/RoutePlanner/types';
import { ConnectedLegSchemaType } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import {
  AirportRouteLocation,
  LegLocationTypeEnum,
  RecommendedFlightLegType,
  RecommendedFlightType,
} from '@/api/apis/route';
import { getTzDateTimeString } from '@/utils/helpers';

export const getTimeZoneOffsetString = (isoString: string) => {
  const match = isoString.match(/([+-]\d{2}:\d{2})$/);
  return match ? match[1] : 'Z'; // Returns "Z" if it's UTC
};

export const getCommercialZonedDateTime = (isoString: string) => {
  const timezoneOffset = getTimeZoneOffsetString(isoString);
  const dateString = new Date(isoString).toISOString();
  return parseAbsolute(dateString, timezoneOffset);
};

export const getAbsoluteCommercialDate = (dateString: string) => {
  const dateStringTz = getTzDateTimeString(dateString);

  return parseZonedDateTime(dateStringTz);
};

export const getCommercialTime = (date: ZonedDateTime) => {
  const time = toTime(date).toString();
  const [hours, minutes] = time.split(':');

  return [hours, minutes].join(':');
};

export const getAirportInfo = (
  airport: AirportListItemType | null,
  dateTime: string,
): { code: string; time: string } => {
  const dateTimeString = getAbsoluteCommercialDate(dateTime);

  return {
    code: airport?.name || 'N/A',
    time: getCommercialTime(dateTimeString),
  };
};

export const getFlightDateTime = (
  legs: RecommendedFlightLegType[],
): { departureDateTime: string; arrivalDateTime: string } => {
  if (legs.length === 1) {
    return {
      departureDateTime: getCommercialZonedDateTime(legs[0].departureTimeLocal).toString(),
      arrivalDateTime: getCommercialZonedDateTime(legs[0].arrivalTimeLocal).toString(),
    };
  }

  const startLeg = legs[0];
  const endLeg = legs[legs.length - 1];

  return {
    departureDateTime: getCommercialZonedDateTime(startLeg.departureTimeLocal).toString(),
    arrivalDateTime: getCommercialZonedDateTime(endLeg.arrivalTimeLocal).toString(),
  };
};

export const convertSabreFlightLegToCommercialAviaFormValues = (
  flight: RecommendedFlightType,
  leg: RecommendedFlightLegType,
): ConnectedLegSchemaType => {
  const parsedDepartureTime = getCommercialZonedDateTime(leg.departureTimeLocal);
  const parsedArrivalTime = getCommercialZonedDateTime(leg.arrivalTimeLocal);

  return {
    departureLocation: {
      id: leg.departureAirportId,
      displayName: leg.departureAirportIATA,
      locationType: LegLocationTypeEnum.airport,
      locationTypeId: `${LegLocationTypeEnum.airport}|${leg.departureAirportId}`,
      maoId: '',
      fboId: '',
      timezone: parsedDepartureTime.timeZone,
      fboAddressId: 0,
      longitude: null,
      latitude: null,
    },
    arrivalLocation: {
      id: leg.arrivalAirportId,
      displayName: leg.arrivalAirportIATA,
      locationType: LegLocationTypeEnum.airport,
      locationTypeId: `${LegLocationTypeEnum.airport}|${leg.arrivalAirportId}`,
      maoId: '',
      fboId: '',
      timezone: parsedArrivalTime.timeZone,
      fboAddressId: 0,
      longitude: null,
      latitude: null,
    },
    departureDateTimeValue: parsedDepartureTime,
    departureDateTime: parsedDepartureTime.toString(),
    departureLocalTimezone: parsedDepartureTime.timeZone,
    arrivalDateTimeValue: parsedArrivalTime,
    arrivalDateTime: parsedArrivalTime.toString(),
    arrivalLocalTimezone: parsedArrivalTime.timeZone,
    flightNumber: leg.flightNumber,
    seatType: leg.seatType,
    numberOfSeats: flight.numberOfSeats,
    flightOperator: flight.flightOperator,
    bookingNumber: '',
  };
};

export const getCommercialFlightInfoString = (dateTime: string, timezone: string, airport: AirportRouteLocation) => {
  const dateTimeZoned = parseAbsolute(dateTime, timezone);
  const timeString = getCommercialTime(dateTimeZoned);

  return `${timeString} - ${airport.iata || airport.icao}`;
};
